/**
 * One game on a team's schedule (Scores UX spec v1.0 section 8), the single
 * row the Schedule and Overview tabs repeat, after the legacy app's team
 * schedule. 64 tall, 16 in from each side, and the whole row is one button
 * that opens the game. Left to right:
 *
 *   [date tile 44] [vs|at 32] [opponent mark 28] 12 [opponent name ....] [score] 8 [badge 24]
 *
 * - The date tile is a 44x44 `surfaceSunken` square, radius 8, with the day
 *   number in `bodyStrong` over the weekday in `small`. Intl free: both come
 *   from the shared day tables in `@sportsmet/theme/scores`.
 * - The opponent's name is one line and never ellipsized: the longest server
 *   name that fits the measured cell (`pickTeamLabel`).
 * - The score is the subject team's first, "24-17" with an ASCII hyphen, in
 *   `bodyStrong` tabular figures, right aligned in a fixed slot sized for
 *   "000-000". A scheduled game puts the server's kickoff label there in
 *   `small` `textSecondary`; a postponed or canceled one, the server's label
 *   in a `surfaceSunken` pill.
 * - The badge is a 24 square: W `successTint`/`success`, L `errorTint`/
 *   `error`, T `surfaceSunken`/`textSecondary`, from the server's winner and
 *   tie flags (`toTeamGameRowModel`). A live game shows an 8px `errorSolid`
 *   dot instead.
 *
 * Emphasis is never color alone: the letter is the result.
 */
import { tokens } from "@sportsmet/theme";
import { pickTeamLabel } from "@sportsmet/theme/scores";
import { cn, FOCUS_RING } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { dayOfMonth, weekdayShort } from "../scores/calendarDay";
import { TeamDisc } from "../scores/TeamDisc";
import { useMeasuredWidth, viewportWidthPx } from "../scores/useMeasuredWidth";
import {
  TEAM_AWAY_PREFIX,
  TEAM_HOME_PREFIX,
  type typeTeamGameRowModel,
} from "./teamGameLogic";

export type typeTeamScheduleRowProps = {
  row: typeTeamGameRowModel;
  onOpenGame: (eventId: string) => void;
  /** Kept on the row so the Schedule tab can scroll it into view. */
  rowRef?: ((element: HTMLButtonElement | null) => void) | undefined;
};

const BADGE_CLASS: Record<"W" | "L" | "T", string> = {
  W: "bg-success-tint text-success",
  L: "bg-error-tint text-error",
  T: "bg-surface-sunken text-ink-secondary",
};

const NAME_FONT_PX = tokens.text.body.size;

/** Everything on the row but the name, for the first render's estimate:
 *  the two 16 insets, the 44 tile, the 32 vs/at column, the 28 mark, the 72
 *  score slot, the 24 badge, five 8px gaps and the extra 4 before the name. */
const ROW_FIXED_PX = 2 * 16 + 44 + 32 + 28 + 72 + 24 + 5 * 8 + 4;

function nameCellEstimatePx(): number {
  const viewport = Math.min(
    viewportWidthPx(tokens.size.contentWidth),
    tokens.size.contentWidth,
  );
  return Math.max(0, viewport - 2 * tokens.size.pageGutter - 2 - ROW_FIXED_PX);
}

function resultText(row: typeTeamGameRowModel): string {
  return row.resultLetter !== null
    ? `${row.resultLetter} ${row.scoreLabel}`.trim()
    : row.statusLabel;
}

export const TeamScheduleRow = (props: typeTeamScheduleRowProps): ReactNode => {
  const { row } = props;
  const { ref, widthPx } =
    useMeasuredWidth<HTMLSpanElement>(nameCellEstimatePx());
  const label = pickTeamLabel(
    {
      name: row.opponentName,
      shortName: row.opponentShortName,
      abbreviation: row.opponentAbbreviation,
    },
    widthPx,
    NAME_FONT_PX,
  );
  const isOff = row.status === "postponed" || row.status === "canceled";
  const hasScore =
    row.scoreLabel !== "" && !isOff && row.status !== "scheduled";
  const isLiveShown = row.isLive && row.status === "inProgress";

  return (
    <button
      type="button"
      ref={props.rowRef}
      data-testid="team-game-row"
      data-event-id={row.eventId}
      onClick={() => {
        props.onOpenGame(row.eventId);
      }}
      aria-label={`${row.opponentLine}, ${row.dateLabel}, ${resultText(row)}`}
      className={cn(
        "flex h-16 w-full items-center gap-2 border-t border-hairline px-4 text-left first:border-t-0",
        "transition-colors duration-200 hover:bg-surface-sunken motion-reduce:transition-none",
        FOCUS_RING,
      )}
    >
      <span
        className="flex h-touch-target w-touch-target shrink-0 flex-col items-center justify-center rounded-sm bg-surface-sunken"
        data-testid="team-game-date-tile"
        aria-hidden="true"
      >
        {row.day !== "" ? (
          <>
            <span className="text-body-strong text-ink tabular-nums">
              {dayOfMonth(row.day)}
            </span>
            <span className="text-small text-ink-secondary">
              {weekdayShort(row.day)}
            </span>
          </>
        ) : null}
      </span>
      <span
        className="w-8 shrink-0 text-center text-small text-ink-secondary"
        aria-hidden="true"
        data-testid="team-game-venue"
      >
        {(row.isHome ? TEAM_HOME_PREFIX : TEAM_AWAY_PREFIX).trim()}
      </span>
      <TeamDisc
        plateText={row.opponentAbbreviation}
        logoUrl={row.opponentLogoUrl}
        plateColorHex={row.opponentPlateColorHex}
        size="row"
        testId="team-game-mark"
        imageTestId="team-game-mark-image"
      />
      <span
        ref={ref}
        className="ml-1 min-w-0 flex-1 overflow-hidden whitespace-nowrap text-body text-ink"
        data-testid="team-game-opponent"
        data-full-name={row.opponentName}
        aria-hidden="true"
      >
        {label}
      </span>
      <span
        className="flex w-18 shrink-0 justify-end text-right"
        data-testid="team-game-score-slot"
        aria-hidden="true"
      >
        {hasScore ? (
          <span
            className="text-body-strong text-ink tabular-nums"
            data-testid="team-game-score"
          >
            {row.scoreLabel}
          </span>
        ) : isOff ? (
          <span
            className="rounded-full bg-surface-sunken px-2 text-small text-ink-secondary"
            data-testid="team-game-status"
          >
            {row.statusLabel}
          </span>
        ) : (
          <span
            className="text-small text-ink-secondary tabular-nums"
            data-testid="team-game-status"
          >
            {row.statusLabel}
          </span>
        )}
      </span>
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center"
        aria-hidden="true"
      >
        {row.resultLetter !== null ? (
          <span
            data-testid="team-game-result"
            data-result={row.resultLetter}
            className={cn(
              "flex h-6 w-6 items-center justify-center rounded-sm text-caption font-bold",
              BADGE_CLASS[row.resultLetter],
            )}
          >
            {row.resultLetter}
          </span>
        ) : isLiveShown ? (
          <span
            className="h-score-live-dot w-score-live-dot rounded-full bg-error-solid"
            data-testid="team-game-live-dot"
          />
        ) : null}
      </span>
    </button>
  );
};
